import { reactive } from 'vue';
import apiClient from './apiClient';
import { isAdminSession } from '@/utils/adminFreshness';

/**
 * Estado compartido de la galería pública (`/galeria`).
 *
 * El backend decide qué se puede ver:
 *  - Sección activa  → todos reciben el contenido.
 *  - Sección apagada → los usuarios comunes reciben todo vacío con
 *    `is_active: false`; los administradores (staff) reciben el contenido
 *    igual, para poder previsualizarla desde el panel.
 *
 * Con eso, sin lógica de roles en el front:
 *  - `isActive`  → si el link tiene que aparecer en el menú / footer.
 *  - `hasContent` → si hay algo para mostrar (o sea: si este visitante puede
 *    entrar a /galeria aunque la sección esté apagada).
 *
 * Se cachea la respuesta para que el navbar, el footer, el guard de la ruta y
 * la propia sección compartan UNA sola request.
 */

const state = reactive({
  loaded: false,
  /** La sección está visible para los usuarios comunes (lo dice el backend). */
  isActive: false,
  /** El backend devolvió contenido (los admins lo reciben aunque esté apagada). */
  hasContent: false,
  /** Última respuesta cruda del endpoint público. */
  payload: null,
});

let pending = null;
let loadedForAdmin = null;
/** Cuándo se guardó la respuesta actual (para no servir data vieja eternamente). */
let loadedAt = 0;
let lastRequestFailed = false;
/** Vida útil del caché: pasado este tiempo se vuelve a pedir. */
const CACHE_TTL_MS = 5 * 60 * 1000;
const ERROR_TTL_MS = 30 * 1000;

/** Pide (una sola vez) el estado + contenido de la galería. */
export async function loadGalleryData({ force = false } = {}) {
  const adminSession = isAdminSession();
  if (loadedForAdmin !== null && loadedForAdmin !== adminSession) {
    state.loaded = false;
    state.isActive = false;
    state.hasContent = false;
    state.payload = null;
  }
  const expirado = loadedAt > 0 && Date.now() - loadedAt > (lastRequestFailed ? ERROR_TTL_MS : CACHE_TTL_MS);
  if (!adminSession && state.loaded && !force && !expirado) return state.payload;
  if (pending && !force) return pending;

  pending = apiClient
    .get('/home-gallery/')
    .then(({ data }) => {
      state.isActive = data?.is_active === true && !!data?.section;
      state.hasContent = !!data?.section;
      state.payload = data || null;
      state.loaded = true;
      loadedAt = Date.now();
      loadedForAdmin = adminSession;
      lastRequestFailed = false;
      return state.payload;
    })
    .catch((error) => {
      // Si no se puede confirmar la activación, la galería queda oculta.
      console.warn('No se pudo leer el estado de la galería:', error);
      state.isActive = false;
      state.hasContent = false;
      state.payload = null;
      state.loaded = true;
      loadedAt = Date.now();
      loadedForAdmin = adminSession;
      lastRequestFailed = true;
      return null;
    })
    .finally(() => {
      pending = null;
    });

  return pending;
}

/** Estado reactivo para templates (navbar / footer / panel). */
export function useGalleryVisibility() {
  return state;
}

/** ¿Este visitante puede entrar a /galeria? */
export async function canViewGallery() {
  await loadGalleryData();
  return state.isActive || (isAdminSession() && state.hasContent);
}

/**
 * Actualiza el estado sin pegarle al backend: lo usa el panel al activar o
 * desactivar la sección, así el menú y el footer reaccionan al toque.
 */
export function setGalleryActive(isActive) {
  state.isActive = !!isActive;
  state.loaded = true;
  loadedForAdmin = isAdminSession();
  loadedAt = Date.now();
  lastRequestFailed = false;
  if (state.payload) {
    state.payload.is_active = !!isActive;
  }
}
